import { useApolloClient } from "@apollo/client"
import { ChatError } from "@lazarentertainment/public-chat-web"
import { usePublicChatContext } from "@lazarentertainment/public-chat-web/dist/src/react"
import { useChannelContext } from "client/components/channel"
import {
  CheckoutCompletedDocument,
  CheckoutCompletedMutation,
  CheckoutCompletedMutationVariables,
  useActiveSaleUpdatedSubscription,
} from "client/graphql/commerce-api"
import { Voting_Question } from "client/graphql/commerce-v2-api"
import {
  DetailedProductFieldsFragment,
  useProductsByIdsQuery,
} from "client/graphql/storefront-api"
import { useVotingContext } from "client/packages/voting/components/VotingContext"
import { FC, ReactNode, useEffect, useMemo } from "react"
import { isNotNull } from "shared/utils/isNotNull"

import {
  isChatNotification,
  useChatContext,
} from "../../chat/components/ChatContext"
import { useShopContext } from "../../commerce/components/ShopContext"
import { isProduct } from "../../commerce/utils/isProduct"
import { useVideoContext } from "../../player/player/VideoContext"
import { PlayerError } from "../../player/types"
import { useIsChatHidden } from "../../utils/useActivePlugins"
import { sendEvent } from "../sendEvent"

export type StageTenApiState = {
  channel: {
    title: string
    broadcastTitle?: string | null
    aspectRatio: string
    live: boolean
  }
  video: {
    isPlaying: boolean
    alreadyPlayed: boolean
    currentTime: number
    duration: number
    error: PlayerError | null
  }
  voting: {
    activeQuestion: Voting_Question | null
  }
  chat: {
    messages: ShopMiniChatTextMessage[]
    displayName: string | null
    writeReady: boolean
    isEnabled: boolean
    isHidden: boolean
    error: ChatError | null
  }
  commerce: {
    saleActive: boolean
    shopId?: string
    activeSaleId?: string | null
    saleProducts?: DetailedProductFieldsFragment[]
    featuredProductId?: string | null
    discountCode?: string | null
    discountType?: "fixedAmount" | "freeShipping" | "percentage" | "none"
    discountValue?: number | null
  }
}

// Message type that is compatible with Shop-Mini
type ShopMiniChatTextMessage = {
  type: "message"
  timetoken: string
  timestampMs: number
  messageKey: string
  userId: string
  displayName: string
  isChannelOwner: boolean
  text: string
  isMe: boolean
}

export type StageTenApiActions = {
  vote: (answerId: string) => void
  sendChatMessage: (message: string) => void
  setChatUsername: (username: string) => void
  checkoutCompleted: (orderId: string) => void
  setIsPlaying: (isPlaying: boolean) => void
  setCurrentTime: (currentTime: number) => void
}
/*
 * Register event handlers to handle messages from the parent and send
 * messages to the parent on data change.
 */
function useAPI({
  state,
  actions,
}: {
  state: StageTenApiState
  actions: StageTenApiActions
}) {
  useEffect(() => {
    sendEvent({
      name: "init",
      payload: {
        aspectRatio: state.channel.aspectRatio,
      },
      meta: {},
    })
  }, [])

  // TODO make state a ref? https://stackoverflow.com/questions/55565444/how-to-register-event-with-useeffect-hooks
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      let data
      if (typeof event.data === "string") {
        let parsedData
        try {
          parsedData = JSON.parse(event.data)
        } catch (e) {
          console.error("onMessage string data was not JSON", e)
          return
        }
        data = parsedData
      } else {
        data = event.data
      }

      switch (data.action) {
        case "getstate": {
          sendEvent({
            name: "state",
            payload: state,
            meta: {},
          })
          break
        }

        case "voting:vote": {
          actions.vote(data.payload.answerId)
          break
        }

        case "chat:sendMessage": {
          actions.sendChatMessage(data.payload.message)
          break
        }

        case "chat:setUsername": {
          actions.setChatUsername(data.payload.username)
          break
        }

        case "commerce:checkoutCompleted": {
          actions.checkoutCompleted(data.payload.orderId)
          break
        }

        case "video:setIsPlaying": {
          actions.setIsPlaying(data.payload.isPlaying)
          break
        }

        case "video:setCurrentTime": {
          actions.setCurrentTime(data.payload.currentTime)
          break
        }

        default:
          console.log("Unknown action", data.action)
      }
    }

    window.addEventListener("message", onMessage)

    return () => window.removeEventListener("message", onMessage)
  }, [state])

  useEffect(() => {
    sendEvent({
      name: "channel:channelUpdated",
      payload: {
        live: state.channel.live,
        aspectRatio: state.channel.aspectRatio,
        title: state.channel.title,
        broadcastTitle: state.channel.broadcastTitle,
      },
      meta: {},
    })
  }, [
    state.channel.live,
    state.channel.aspectRatio,
    state.channel.title,
    state.channel.broadcastTitle,
  ])

  useEffect(() => {
    sendEvent({
      name: "video:playingChange",
      payload: {
        isPlaying: state.video.isPlaying,
        alreadyPlayed: state.video.alreadyPlayed,
      },
      meta: {},
    })
  }, [state.video.isPlaying, state.video.alreadyPlayed])

  useEffect(() => {
    sendEvent({
      name: "video:timeChange",
      payload: {
        currentTime: state.video.currentTime,
        duration: state.video.duration,
      },
      meta: {},
    })
  }, [state.video.currentTime, state.video.duration])

  useEffect(() => {
    if (state.video.error !== null) {
      sendEvent({
        name: "video:error",
        payload: {
          error: state.video.error,
        },
        meta: {},
      })
    }
  }, [state.video.error])

  useEffect(() => {
    sendEvent({
      name: "voting:activeQuestionChange",
      payload: { activeQuestion: state.voting.activeQuestion },
      meta: {},
    })
  }, [state.voting.activeQuestion])

  useEffect(() => {
    sendEvent({
      name: "chat:messages",
      payload: { messages: state.chat.messages },
      meta: {},
    })
  }, [state.chat.messages])

  useEffect(() => {
    sendEvent({
      name: "chat:displayName",
      payload: { displayName: state.chat.displayName },
      meta: {},
    })
  }, [state.chat.displayName])

  useEffect(() => {
    sendEvent({
      name: "chat:writeReady",
      payload: { ready: state.chat.writeReady },
      meta: {},
    })
  }, [state.chat.writeReady])

  useEffect(() => {
    sendEvent({
      name: "chat:availability",
      payload: {
        // See https://github.com/lazarentertainment/plugin-service/issues/373 to understand these better
        enabled: state.chat.isEnabled,
        hidden: state.chat.isHidden,
      },
      meta: {},
    })
  }, [state.chat.isEnabled, state.chat.isHidden])

  useEffect(() => {
    if (state.chat.error) {
      sendEvent({
        name: "chat:error",
        payload: {
          error: state.chat.error,
        },
        meta: {},
      })
    }
  }, [state.chat.error])

  useEffect(() => {
    sendEvent({
      name: "commerce:saleActiveState",
      payload: {
        saleActive: state.commerce.saleActive,
        shopId: state.commerce.shopId,
        activeSaleId: state.commerce.activeSaleId,
        featuredProductId: state.commerce.featuredProductId,
      },
      meta: {},
    })
  }, [state.commerce.saleActive, state.commerce.featuredProductId])

  useEffect(() => {
    sendEvent({
      name: "commerce:discountCode",
      payload: {
        discountCode: state.commerce.discountCode,
        discountType: state.commerce.discountType,
        discountValue: state.commerce.discountValue,
      },
      meta: {},
    })
  }, [
    state.commerce.discountCode,
    state.commerce.discountType,
    state.commerce.discountValue,
  ])

  useEffect(() => {
    sendEvent({
      name: "commerce:saleProducts",
      payload: {
        saleProducts:
          state.commerce.saleProducts
            ?.filter(isNotNull)
            ?.map((product) => {
              return {
                ...product,
                decodedId: product.id, // TODO not needed anymore (but shop-mini depends on this instead of just id)
              }
            }) ?? [],
      },
      meta: {},
    })
  }, [state.commerce.saleProducts])
}

export type StageTenAPI2023MarchProps = {
  children?: ReactNode
}
/*
 * this should probably just get merged with the `useApi` hook above.
 */
export const StageTenAPI2023March: FC<StageTenAPI2023MarchProps> = (
  props,
) => {
  const channel = useChannelContext()

  // VIDEO
  const video = useVideoContext()

  // VOTING
  const { activeQuestion, castVote } = useVotingContext()

  // COMMERCE
  const { shop } = useShopContext()
  const { data: productData } = useProductsByIdsQuery({
    skip: !shop?.activeSale,
    variables: { ids: shop?.activeSale?.visibleProductIds ?? [] },
    context: { useStorefrontAPI: true },
  })
  const apolloClient = useApolloClient()
  // useMemo so we aren't building a new list of products on every render, emitting events on every render
  const saleProducts = useMemo(() => {
    return productData?.products?.filter(isProduct)
  }, [productData?.products])

  useActiveSaleUpdatedSubscription({
    skip: !shop,
    variables: { shopId: shop?.id ?? "" },
  })

  // CHAT
  const chatState = useChatContext()
  const { displayName, pluginState, updateDisplayName, submitMessage } =
    usePublicChatContext()
  // Messages here are adapted to be compatible with Shop-Mini.
  // useMemo so we aren't building a new list of messages on every render, emitting events on every render
  const chatMessages = useMemo(() => {
    return chatState.messages.reduce<ShopMiniChatTextMessage[]>(
      (messages, message) => {
        if (!isChatNotification(message)) {
          messages.push({
            type: "message",
            ...message,
            timetoken: message.timetoken?.toString() ?? message.messageKey,
          })
        }

        return messages
      },
      [],
    )
  }, [chatState.messages])
  const isChatHidden = useIsChatHidden()

  useAPI({
    state: {
      channel: {
        title: channel.title,
        broadcastTitle: channel.broadcastTitle,
        aspectRatio: channel.aspectRatio,
        live: channel.live,
      },
      video: {
        isPlaying: video.isPlaying,
        alreadyPlayed: video.alreadyPlayed,
        currentTime: video.videoTimestamp,
        duration: video.videoElement?.duration,
        error: video.playerError,
      },
      voting: {
        activeQuestion,
      },
      chat: {
        messages: chatMessages,
        displayName,
        writeReady: chatState.writeReady,
        isEnabled: pluginState === "enabled",
        isHidden: isChatHidden,
        error: chatState.chatError,
      },
      commerce: {
        saleActive: !!shop?.activeSaleId,
        shopId: shop?.id,
        activeSaleId: shop?.activeSaleId,
        saleProducts,
        featuredProductId: shop?.activeSale?.featuredProductId,
        discountCode: shop?.activeSale?.discountCode,
        discountType: shop?.activeSale?.discountType,
        discountValue: shop?.activeSale?.discountValue,
      },
    },
    actions: {
      vote(answerId: string) {
        if (!activeQuestion?.id) {
          console.log("No activeQuestion")
          return
        }

        console.log("Sending Vote", activeQuestion.id, answerId)
        castVote(answerId)
      },
      setChatUsername(username: string) {
        updateDisplayName(username).then(() => {
          console.log(`setDisplayName success, username=${username}`)
        })
      },
      sendChatMessage(message: string) {
        submitMessage(message).promise.catch((error) => {
          console.error(error)
        })
      },
      checkoutCompleted(orderId: string) {
        if (shop?.activeSaleId) {
          apolloClient.mutate<
            CheckoutCompletedMutation,
            CheckoutCompletedMutationVariables
          >({
            mutation: CheckoutCompletedDocument,
            variables: {
              shopId: shop.id,
              saleId: shop.activeSaleId,
              orderId,
            },
          })
        }
      },
      setIsPlaying(playing: boolean) {
        if (playing) {
          video.videoElement?.play()
        } else {
          video.videoElement?.pause()
        }
      },
      setCurrentTime(currentTime: number) {
        if (video.videoElement) {
          video.videoElement.currentTime = currentTime
        }
      },
    },
  })

  return <>{props.children}</>
}
